/**
 * Admin page: Partner Duplicate Detector.
 *
 * Scans partners for near-identical records (same company written three
 * different ways: "BARRY CALLEBAUT" / "BARRY CALLEBAUT LTD" / "BARRY_CALLEBAUT")
 * and lets the operator one-click merge each cluster into a canonical code.
 *
 * Powered by:
 *   • GET  /api/admin/migrations/detect-partner-duplicates
 *   • POST /api/admin/migrations/consolidate-partner-code
 *
 * The merge is additive on the canonical side: all gate_events, containers,
 * invoices, wash_orders, EDI records, tariffs, and rental state for the
 * alias partners get retagged to the canonical code. Alias partner records
 * are archived (not deleted) so audit history is preserved.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Users, Merge, CheckCircle2, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function PartnerDuplicates() {
  const [loading, setLoading] = useState(true);
  const [clusters, setClusters] = useState([]);
  const [merging, setMerging] = useState(null); // normalized_key being merged
  const [done, setDone] = useState({}); // normalized_key -> result summary

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/migrations/detect-partner-duplicates");
      setClusters(data.clusters || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runMerge = async (cluster, canonical) => {
    const aliases = cluster.members
      .map((m) => m.code)
      .filter((code) => code !== canonical);
    if (aliases.length === 0) {
      toast.warning("Pick a canonical code different from the only member.");
      return;
    }

    // Dry-run first
    setMerging(cluster.normalized_key);
    try {
      const { data: dry } = await api.post("/admin/migrations/consolidate-partner-code", {
        canonical_code: canonical,
        alias_codes: aliases,
        apply: false,
      });
      const plan = dry.plan || {};
      const carrierUpd = Object.values(plan.carrier_code_updates || {}).reduce((a, b) => a + b, 0);
      const customerUpd = Object.values(plan.customer_code_updates || {}).reduce((a, b) => a + b, 0);
      const backfill = (plan.free_text_backfill_matches || {}).wash_orders_no_code || 0;
      const total = carrierUpd + customerUpd + backfill + aliases.length;

      if (!window.confirm(
        `Merge into ${canonical}?\n\n` +
        `Aliases to archive: ${aliases.join(", ")}\n` +
        `• Carrier-side updates: ${carrierUpd}\n` +
        `• Customer-side updates: ${customerUpd}\n` +
        `• Legacy wash backfill: ${backfill}\n\n` +
        `Total records touched: ${total}.\nThis is reversible via the archived partner records in the audit log.`
      )) {
        setMerging(null);
        return;
      }

      // Apply
      const { data: result } = await api.post("/admin/migrations/consolidate-partner-code", {
        canonical_code: canonical,
        alias_codes: aliases,
        apply: true,
      });
      const updated = result.updated || {};
      const upCarrier = Object.entries(updated).filter(([k]) => k.includes("carrier") || k.includes("gate_events") || k.includes("containers") || k.includes("tariffs") || k.includes("invoices") || k.includes("edi") || k.includes("release")).reduce((a, [, v]) => a + v, 0);
      const upCustomer = Object.entries(updated).filter(([k]) => k.includes("customer") || k.includes("wash") || k.includes("rental")).reduce((a, [, v]) => a + v, 0);
      setDone((d) => ({ ...d, [cluster.normalized_key]: { canonical, aliases, carrier: upCarrier, customer: upCustomer, archived: updated.archived_partners || 0 } }));
      toast.success(`Merged ${aliases.length} partner(s) into ${canonical}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMerging(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="partner-duplicates">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-black text-2xl tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6" /> Partner Duplicate Detector
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Finds partner records whose names collapse to the same key after
            stripping punctuation, whitespace, and legal-entity suffixes
            (e.g. <span className="font-mono">BARRY CALLEBAUT</span> ≡{" "}
            <span className="font-mono">BARRY_CALLEBAUT_LTD</span>). One click
            retags all downstream records to the canonical partner and
            archives the duplicates.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="ps-btn ps-btn-secondary"
          data-testid="refresh-duplicates"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {loading && (
        <div className="text-center py-10 text-gray-500 text-sm">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Scanning partners…
        </div>
      )}

      {!loading && clusters.length === 0 && (
        <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-6 text-center" data-testid="no-duplicates">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
          <div className="font-bold text-base">No partner duplicates detected.</div>
          <div className="text-xs text-emerald-900/70 mt-1">All partner records normalise to unique keys.</div>
        </div>
      )}

      {!loading && clusters.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>{clusters.length}</b> duplicate cluster{clusters.length === 1 ? "" : "s"} found. Highest-usage member is suggested as the canonical. Verify before merging — merges are reversible only by manual rollback via the archived partner records.
          </div>
        </div>
      )}

      {!loading && clusters.map((c) => {
        const result = done[c.normalized_key];
        return (
          <ClusterCard
            key={c.normalized_key}
            cluster={c}
            onMerge={runMerge}
            busy={merging === c.normalized_key}
            result={result}
          />
        );
      })}
    </div>
  );
}

function ClusterCard({ cluster, onMerge, busy, result }) {
  const [canonical, setCanonical] = useState(cluster.canonical_code_suggestion);

  if (result) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4" data-testid={`merged-${cluster.normalized_key}`}>
        <div className="flex items-center gap-2 text-emerald-900 font-bold">
          <CheckCircle2 className="w-4 h-4" /> Merged into {result.canonical}
        </div>
        <div className="text-xs text-emerald-900/70 mt-1">
          Archived: {result.aliases.join(", ")} • Carrier rows updated: {result.carrier} • Customer rows updated: {result.customer}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 bg-white rounded-md shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">
            Normalized key
          </div>
          <div className="font-mono font-bold text-sm">{cluster.normalized_key}</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">
            Canonical:
          </label>
          <select
            value={canonical}
            onChange={(e) => setCanonical(e.target.value)}
            className="ps-input font-mono text-xs h-8 py-0"
            data-testid={`canonical-select-${cluster.normalized_key}`}
            disabled={busy}
          >
            {cluster.members.filter((m) => !m.archived).map((m) => (
              <option key={m.code} value={m.code}>{m.code}</option>
            ))}
          </select>
          <button
            onClick={() => onMerge(cluster, canonical)}
            disabled={busy}
            className="ps-btn ps-btn-primary text-xs h-8 px-3"
            data-testid={`merge-btn-${cluster.normalized_key}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />}
            Merge
          </button>
        </div>
      </div>
      <table className="ps-table text-xs">
        <thead>
          <tr>
            <th>Code</th>
            <th>Legal name</th>
            <th>Trading name</th>
            <th>Aliases</th>
            <th className="text-right">Usage</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {cluster.members.map((m) => (
            <tr
              key={m.code}
              className={m.code === canonical ? "bg-blue-50" : ""}
              data-testid={`cluster-member-${m.code}`}
            >
              <td className="font-mono font-bold">{m.code}</td>
              <td>{m.legal_name || "—"}</td>
              <td className="text-gray-600">{m.trading_name || "—"}</td>
              <td className="font-mono text-[10px] text-gray-500">
                {(m.aliases || []).join(", ") || "—"}
              </td>
              <td className="text-right font-mono">{m.usage_score < 0 ? "—" : m.usage_score}</td>
              <td className="text-right">
                {m.archived && <span className="text-[10px] font-bold text-gray-400 uppercase">archived</span>}
                {!m.archived && m.code === canonical && <span className="text-[10px] font-bold text-blue-700 uppercase">canonical</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
